import React from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import { EmojiEvents } from '@mui/icons-material';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

// Komponent dla celów zbiorowych
const TeamGoalCard = ({ goal, userContribution, onViewDetails }) => {
  const theme = useTheme();
  const progress = goal.progress || 0;
  const userProgress = userContribution?.progress || 0;
  
  // Animacja dla karty
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring", 
        stiffness: 100,
        damping: 10
      }
    }
  };

  // Animacja dla paska postępu
  const progressVariants = {
    hidden: { width: '0%' },
    visible: { 
      width: `${Math.min(progress, 100)}%`,
      transition: { 
        duration: 1.5, 
        ease: "easeOut" 
      }
    }
  };

  // Animacja dla paska postępu użytkownika
  const userProgressVariants = {
    hidden: { width: '0%' },
    visible: { 
      width: `${Math.min(userProgress, 100)}%`,
      transition: { 
        duration: 1.5, 
        ease: "easeOut",
        delay: 0.5
      }
    }
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover={{ scale: 1.02 }}
    >
      <Card 
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          borderRadius: 2,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
        }}
      >
        {/* Pasek górny z typem celu */}
        <Box 
          sx={{ 
            p: 1, 
            display: 'flex', 
            alignItems: 'center',
            bgcolor: theme.palette.primary.main,
            color: 'white'
          }}
        >
          <EmojiEvents fontSize="large" />
          <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 'bold' }}>
            Cel zespołowy
          </Typography>
        </Box>
        
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Tytuł celu */}
          <Typography variant="h6" component="h2" gutterBottom>
            {goal.name}
          </Typography>
          
          {/* Opis celu */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {goal.description}
          </Typography>
          
          {/* Wartość celu i postęp zespołu */}
          <Box sx={{ mt: 'auto' }}>
            <Typography variant="subtitle2" gutterBottom>
              Postęp zespołu:
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" fontWeight="bold" color={theme.palette.primary.main}>
                <CountUp 
                  end={progress} 
                  duration={2} 
                  suffix="%" 
                  decimals={1}
                  decimal="," 
                />
              </Typography>
            </Box>
            
            {/* Pasek postępu zespołu */}
            <Box sx={{ position: 'relative', height: 10, bgcolor: alpha(theme.palette.grey[300], 0.5), borderRadius: 5, mb: 2 }}>
              <motion.div
                variants={progressVariants}
                initial="hidden"
                animate="visible"
                style={{
                  position: 'absolute',
                  height: '100%',
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: 5
                }}
              />
            </Box>
            
            {/* Twój wkład */}
            <Typography variant="subtitle2" gutterBottom>
              Twój wkład:
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" fontWeight="bold" color={theme.palette.secondary.main}>
                <CountUp 
                  end={userProgress} 
                  duration={2} 
                  suffix="%" 
                  decimals={1}
                  decimal="," 
                />
              </Typography>
            </Box>
            
            {/* Pasek postępu użytkownika */}
            <Box sx={{ position: 'relative', height: 10, bgcolor: alpha(theme.palette.grey[300], 0.5), borderRadius: 5, mb: 2 }}>
              <motion.div
                variants={userProgressVariants}
                initial="hidden"
                animate="visible"
                style={{
                  position: 'absolute',
                  height: '100%',
                  backgroundColor: theme.palette.secondary.main,
                  borderRadius: 5
                }}
              />
            </Box>
            
            {/* Wartości liczbowe */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" fontWeight="bold">
                <CountUp 
                  end={goal.currentValue || 0} 
                  duration={2} 
                  separator=" " 
                  prefix={goal.targetType === 'sales_amount' ? '' : ''}
                  suffix={goal.targetType === 'sales_amount' ? ' zł' : ''}
                  decimals={goal.targetType === 'sales_amount' ? 0 : 0}
                />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                z {goal.targetType === 'sales_amount' 
                  ? new Intl.NumberFormat('pl-PL').format(goal.targetValue) + ' zł' 
                  : goal.targetValue}
              </Typography>
            </Box>
            
            {/* Twój wkład wartościowo */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Twój wkład:
              </Typography>
              <Typography variant="caption" fontWeight="bold">
                <CountUp 
                  end={userContribution?.currentValue || 0} 
                  duration={2} 
                  separator=" " 
                  prefix={goal.targetType === 'sales_amount' ? '' : ''}
                  suffix={goal.targetType === 'sales_amount' ? ' zł' : ''}
                  decimals={goal.targetType === 'sales_amount' ? 0 : 0}
                />
              </Typography>
            </Box>
            
            {/* Termin */}
            {goal.endDate && (
              <Typography variant="caption" color={
                new Date() > goal.endDate ? "error" : "text.secondary"
              }>
                Termin: {format(goal.endDate, 'd MMMM yyyy', { locale: pl })}
              </Typography>
            )}
          </Box>
        </CardContent>
        
        <Divider />
        
        {/* Przycisk szczegółów */}
        <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
          <Button 
            size="small" 
            onClick={() => onViewDetails(goal)}
            sx={{ 
              color: theme.palette.primary.main,
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.1)
              }
            }}
          >
            Szczegóły
          </Button>
        </Box>
        
        {/* Badge z nagrodą */}
        {goal.reward && (
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 70, 
              right: -20,
              transform: 'rotate(45deg)',
              bgcolor: theme.palette.secondary.main,
              color: 'white',
              px: 5,
              py: 0.5,
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
          >
            <Typography variant="caption" fontWeight="bold">
              NAGRODA!
            </Typography>
          </Box>
        )}
      </Card>
    </motion.div>
  );
};

export default TeamGoalCard;
